import Vue from 'utils/vueUtils';
import Members from '../collections/Members';
import Member from '../models/Member';
import { baseApi } from 'core/api';
import { useInboxesStore, useToastStore } from 'store/modules';

const state = () => ({
    _members: [],
    _parameters: {
        sortBy: 'created_at',
        sortOrder: 'desc',
    },
    _loading: false,
    _removeFromInboxModal: { inboxId: null, openModal: false },
    _loadingLogoutMember: false,
});

const getters = {
    members: (state) => state._members,
    membersLoading: (state) => state._loading,
    removeFromInboxModal: (state) => state._removeFromInboxModal,
    membersParameters: (state) => state._parameters,
    loadingLogoutMember: (state) => state._loadingLogoutMember,
};

const actions = {
    handleRemoveFromInboxModal(data) {
        this._setMemberOpenRemoveModal(data);
    },

    getOrganizationMembers({ retry = false } = {}) {
        if (this._loading) return;

        this._setMembersLoading(true);

        if (retry === true) {
            this._setMembersLoadRetry();
        }

        return baseApi
            .get('core/organization/members')
            .then((response) => {
                this._setMembersLoaded(response.data, true);
                return response;
            })
            .catch((error) => {
                this._setMembersLoadError();
                return error;
            })
            .finally(() => {
                this._setMembersLoading(false);
            });
    },

    getTeamsByIdMembers({ inboxId, params = {} }) {
        return baseApi.get(`core/teams/${inboxId}/members`, { params }).then((response) => {
            const members = new Members(response.data);

            return members.models;
        });
    },

    membersSortUpdate(sort) {
        let members = [...this._members];

        const sortData = (sortBy, sortOrder) => {
            if (sortOrder === 'asc') return (a, b) => (a[sortBy] > b[sortBy] ? 1 : -1);
            return (a, b) => (a[sortBy] < b[sortBy] ? 1 : -1);
        };

        members.sort(sortData(sort.sortBy, sort.sortOrder));

        this._setMemberSortUpdate(sort);
        this._setMembersLoaded(members);
    },

    updateTeamsByIdMembers({ form, inboxId }) {
        return form.post(`core/teams/${inboxId}/members`).then((response) => {
            this._setMemberTeamUpdate({
                members: response.data,
                memberIds: form.members.map((item) => item.id),
                inboxId: inboxId,
            });

            return response.data;
        });
    },

    updateTeamsByMemberId({ teams, members, memberId }) {
        return baseApi
            .post(`core/teams/members/${memberId}`, {
                teams: teams,
            })
            .then((response) => {
                const keys = Object.keys(response?.data);

                keys.forEach((key) => {
                    const membersArr = response.data[key];
                    if (membersArr) {
                        this._setMemberTeamUpdate({
                            members: membersArr,
                            memberIds: members.map((item) => item.id),
                        });
                    }
                });

                return response.data;
            });
    },

    deleteOrganizationMembersById({ id, form }) {
        return form.delete(`core/organization/members/${id}`).then((response) => {
            this._setMemberRemoved(id);
            return response;
        });
    },

    updateOrganizationMembersById({ id, form }) {
        return form.put(`core/organization/members/${id}`).then((response) => {
            const member = new Member(response.data);

            this._setMemberRoleChanged(member);

            return member;
        });
    },

    updateRolePermissions({ payload }) {
        return baseApi.post('organization/permissions/save', payload).then((response) => {
            console.log('response: ', response);
        });
    },

    logoutMember(memberID) {
        this._setMembersLogoutLoading();

        return baseApi
            .post('/core/organization/members/logout', { ids: [memberID], all: false })
            .then(() => {
                useToastStore().showToast({
                    type: 'success',
                    message: 'Member was logout',
                });
            })
            .finally(() => {
                this._setMembersLogoutLoaded();
            });
    },

    logoutMembers(params) {
        this._setMembersLogoutLoading();

        return baseApi
            .post('/core/organization/members/logout', params)
            .then(() => {
                useToastStore().showToast({
                    type: 'success',
                    message: 'Members was logout',
                });
            })
            .finally(() => {
                this._setMembersLogoutLoaded();
            });
    },

    _setMemberTeamUpdate({ members, memberIds }) {
        members = members.filter((item) => memberIds.includes(item.id));

        members.forEach((member) => {
            const index = this.members.findIndex((item) => item.id === member.id);
            const memberExist = this.members.find((item) => item.id === member.id);

            if (index !== -1) {
                Vue.set(
                    this.members,
                    index,
                    new Member({
                        ...memberExist,
                        ...member,
                    })
                );
            }
        });
        useInboxesStore()._setMemberTeamUpdate({ members, memberIds });
    },

    _setInboxMakeDefault({ data, memberId }) {
        if (memberId) {
            this._members = this._members.map((item) => {
                if (item.id === memberId) {
                    item.default_team_id = data.default_team_id;

                    return item;
                }

                return item;
            });
        }
    },

    // Private methods
    _setMemberRoleChanged(member) {
        this._members = this._members.map((item) => {
            if (member.id === item.id) {
                return member;
            }
            return item;
        });
    },

    _setMemberOpenRemoveModal(status) {
        this._removeFromInboxModal = status;
    },

    _setMembersLoading(status) {
        this._loading = status;
    },

    _setMembersLoadRetry() {
        this._members = null;
    },

    _setMembersLoadError() {
        this._members = false;
    },

    _setMembersLoaded(data, isResponseData = false) {
        if (!isResponseData) {
            // If data is already processed array
            this._members = data;
        } else {
            // If data needs to be processed through Members collection
            const members = new Members(data);
            this._members = members.models;
        }
    },

    _setMemberRemoved(id) {
        this._members = this._members.filter((o) => o.id !== id);
    },

    _setMemberSortUpdate(data) {
        this._parameters.sortBy = data.sortBy;
        this._parameters.sortOrder = data.sortOrder;
    },

    _setMembersLogoutLoading() {
        this._loadingLogoutMember = true;
    },

    _setMembersLogoutLoaded() {
        this._loadingLogoutMember = false;
    },
};



export default {
    state,
    getters,
    actions,
};
